import React, { useState, useEffect, useContext, useRef } from 'react';
import Swal from 'sweetalert2';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import { FaEye, FaEdit, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Select from 'react-select';
import GSTVerificationModal from '../../components/GSTVerificationModal';
import PANVerificationModal from '../../components/PANVerificationModal';
import useDraftManager from '../../hooks/useDraftManager';
import StationSelect from '../../components/common/StationSelect';

// States list for station creation
const INDIAN_STATES = [
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli',
  'Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Bhutan',
  'Nepal',
  'Bangladesh'
];

// Add this utility function after the imports
const cleanFormData = (data) => {
  const cleanedData = {};
  
  // Required fields (always include)
  const requiredFields = ['businessName', 'phone', 'address', 'station', 'pinCode', 'createdBy'];
  requiredFields.forEach(field => {
    cleanedData[field] = data[field];
  });


  // Optional fields (only include if they have values)
  const optionalFields = ['businessOwner', 'secondaryPhone', 'email', 'managerName', 'gstNo', 'panNo'];
  optionalFields.forEach(field => {
    if (data[field]?.trim()) {
      cleanedData[field] = data[field].trim();
    }
  });
  return cleanedData;
};

// Custom styles for react-select
const customSelectStyles = (darkMode) => ({
  control: (provided, state) => ({
    ...provided,
    minHeight: '42px',
    backgroundColor: darkMode ? '#4b5563' : 'white',
    border: `1px solid ${darkMode ? '#6b7280' : '#d1d5db'}`,
    borderRadius: '0.5rem',
    fontSize: '14px',
    boxShadow: state.isFocused ? `0 0 0 3px ${darkMode ? 'rgba(96, 165, 250, 0.2)' : 'rgba(59, 130, 246, 0.1)'}` : 'none',
    borderColor: state.isFocused ? (darkMode ? '#60a5fa' : '#3b82f6') : (darkMode ? '#6b7280' : '#d1d5db'),
    '&:hover': {
      borderColor: state.isFocused ? (darkMode ? '#60a5fa' : '#3b82f6') : (darkMode ? '#9ca3af' : '#9ca3af')
    },
    transition: 'all 0.3s'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? (darkMode ? '#3b82f6' : '#3b82f6') 
      : state.isFocused 
        ? (darkMode ? '#374151' : '#eff6ff') 
        : (darkMode ? '#1f2937' : 'white'),
    color: state.isSelected ? 'white' : (darkMode ? '#f3f4f6' : '#374151'),
    fontSize: '14px',
    padding: '8px 12px',
    cursor: 'pointer'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: darkMode ? '#f3f4f6' : '#374151',
    fontSize: '14px'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: darkMode ? '#9ca3af' : '#9ca3af',
    fontSize: '14px'
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: darkMode ? '#1f2937' : 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
    zIndex: 9999
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '4px',
    borderRadius: '0.5rem',
    backgroundColor: darkMode ? '#1f2937' : 'white'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? (darkMode ? '#60a5fa' : '#3b82f6') : (darkMode ? '#9ca3af' : '#9ca3af'),
    '&:hover': {
      color: darkMode ? '#60a5fa' : '#3b82f6'
    }
  }),
  input: (provided) => ({
    ...provided,
    color: darkMode ? '#f3f4f6' : '#374151'
  })
});

// Options for select fields
const pageSizeOptions = [
  { value: 5, label: '5 per page' },
  { value: 10, label: '10 per page' },
  { value: 20, label: '20 per page' },
  { value: 50, label: '50 per page' }
];

const DealerManagement = () => {
  const { user } = useContext(AuthContext);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Reference to "businessName" input for focusing on edit
  const businessNameRef = useRef(null);

  // -----------------------
  // FORM STATE (Add/Edit)
  // -----------------------
  const [form, setForm] = useState({
    businessName: '',
    businessOwner: '',
    phone: '',
    secondaryPhone: '',
    email: '',
    managerName: '',
    address: '',
    station: '',
    pinCode: '',
    gstNo: '',
    panNo: '',
    createdBy: user?.user?._id,
  });
  const [stations, setStations] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isPanReadOnly, setIsPanReadOnly] = useState(false); // To make PAN read-only when extracted from GST
  const [isGstReadOnly, setIsGstReadOnly] = useState(false); // To make GST read-only when verified
  const [showGstPanFields, setShowGstPanFields] = useState(false); // To show GST/PAN fields only after verification

  // Station creation states
  const [showCreateStation, setShowCreateStation] = useState(false);
  const [stationForm, setStationForm] = useState({
    name: '',
    state: '',
    createdBy: user?.user?._id
  });
  const [isCreatingStation, setIsCreatingStation] = useState(false);

  // GST Verification Modal state
  const [showGSTVerification, setShowGSTVerification] = useState(false);
  
  // PAN Verification Modal state
  const [showPANVerification, setShowPANVerification] = useState(false);

  // Draft notification states
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  // Initialize draft manager
  const draftManager = useDraftManager({
    userId: user?.user?._id,
    companyId: user?.user?.company?._id || user?.user?.company,
    featureType: 'dealerManagement',
    formData: form,
    setFormData: setForm,
    autoDraft: true,
    autoDraftInterval: 5000, // 5 seconds
    onError: (error) => {
      console.error('Draft error:', error);
    },
    onDraftSaved: () => {
      setNotificationMessage('Draft saved automatically');
      setShowNotification(true);
    },
    onDraftLoaded: (loadedData) => {
      console.log('Draft loaded:', loadedData);
      Swal.fire({
        icon: 'success',
        title: 'Draft Loaded',
        text: 'Your previous draft has been loaded',
        timer: 2000,
        showConfirmButton: false
      });
    }
  });

  // GST validation and PAN extraction function
  const validateGSTAndExtractPAN = async (gstValue) => {
    if (!gstValue || gstValue.length !== 15) {
      return { isValid: false, error: 'GST number must be 15 characters long' };
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/dealers/validate-gst`, {
        gstNo: gstValue
      });
      
      if (response.data.isValid) {
        return { isValid: true, panNo: response.data.panNo };
      }
    } catch (error) {
      return { isValid: false, error: error.response?.data?.error || 'Invalid GST number' };
    }
  };

  // Handle GST input change with validation and PAN extraction
  const handleGSTChange = async (e) => {
    const gstValue = e.target.value.toUpperCase();
    
    // Always update the form to show user input
    setForm({ ...form, gstNo: gstValue });

    // Only validate when we have exactly 15 characters
    if (gstValue.length === 15) {
      const result = await validateGSTAndExtractPAN(gstValue);
      if (result.isValid) {
        setForm(prev => ({ ...prev, gstNo: gstValue, panNo: result.panNo }));
        setIsPanReadOnly(true);
        Swal.fire({
          icon: 'success',
          title: 'Valid GST',
          text: `PAN ${result.panNo} extracted from GST`,
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Invalid GST',
          text: result.error || 'Please enter a valid GST number'
        });
        setIsPanReadOnly(false);
      }
    } else if (gstValue.length > 15) {
      // Prevent entering more than 15 characters
      setForm({ ...form, gstNo: gstValue.substring(0, 15) });
    } else {
      // Reset PAN readonly state for incomplete GST
      setIsPanReadOnly(false);
    }
  };

  // Handle clearing GST and PAN
  const handleClearGST = () => {
    setForm({ ...form, gstNo: '', panNo: '' });
    setIsPanReadOnly(false);
    setIsGstReadOnly(false);
    setShowGstPanFields(false); // Hide fields again when cleared
  };

  // Handle PAN input change
  const handlePANChange = (e) => {
    setForm({ ...form, panNo: e.target.value.toUpperCase() });
  };

  // Handle GST verification modal data
  const handleGSTVerificationData = ({ gstData, gstin, useData }) => {
    console.log('GST Verification Data Received (Dealer):', { gstData, gstin, useData });
    if (useData && gstData) {
      const extractedData = gstData.extractedData || {};
      console.log('Extracted Data (Dealer):', extractedData);
      
      // Extract PAN from GSTIN (characters 3-12, 0-indexed as 2-12)
      const extractedPAN = gstin.substring(2, 12);
      
      // Update form with comprehensive verified GST data
      setForm(prevForm => ({
        ...prevForm,
        gstNo: gstin,
        panNo: extractedPAN, // Auto-set PAN from GSTIN
        // Prefer trade name; fall back to legal name; never wipe existing value
        businessName: extractedData.tradeName || extractedData.legalName || prevForm.businessName,
        // For dealers, if no business owner is set, try to extract from legal name
        businessOwner: !prevForm.businessOwner ? (extractedData.legalName || prevForm.businessOwner) : prevForm.businessOwner,
        address: extractedData.address || prevForm.address
      }));
      
      setIsPanReadOnly(true);
      setIsGstReadOnly(true);
      setShowGstPanFields(true); // Show GST/PAN fields after successful verification
      
      // Show detailed success message with business information
      const populatedFields = [];
      if (extractedData.legalName || extractedData.tradeName) populatedFields.push('Business Name');
      if (extractedData.legalName && !form.businessOwner) populatedFields.push('Business Owner');
      if (extractedPAN) populatedFields.push('PAN');
      if (extractedData.address) populatedFields.push('Address');
      
      Swal.fire({
        title: 'GST Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>GSTIN:</strong> ${gstin}</p>
            <p class="mb-2"><strong>Extracted PAN:</strong> ${extractedPAN}</p>
            <p class="mb-2"><strong>Business Type:</strong> ${gstData.ctb || 'N/A'}</p>
            <p class="mb-2"><strong>Status:</strong> ${gstData.sts || 'N/A'}</p>
            <p class="mb-2"><strong>Registration Date:</strong> ${gstData.rgdt || 'N/A'}</p>
            ${gstData.nba ? `<p class="mb-2"><strong>Nature of Business:</strong> ${gstData.nba.join(', ')}</p>` : ''}
            ${populatedFields.length > 0 ? `<p class="mt-3 text-sm text-green-600"><strong>Auto-filled:</strong> ${populatedFields.join(', ')}</p>` : ''}
            <p class="mt-3 text-sm text-blue-600">ℹ️ PAN has been automatically extracted from GSTIN. No separate PAN verification needed.</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3b82f6'
      });
    }
  };
  
  // Handle PAN verification modal data
  const handlePANVerificationData = ({ panNo, verificationData }) => {
    console.log('PAN Verification Data Received (Dealer):', { panNo, verificationData });
    
    // Update form with verified PAN
    setForm(prevForm => ({
      ...prevForm,
      panNo: panNo
    }));
    
    // Make PAN field read-only after verification
    setIsPanReadOnly(true);
    
    // Show GST/PAN fields after PAN verification
    setShowGstPanFields(true);
    
    // Check if PAN has linked GSTINs
    const gstinList = verificationData.gstinResList || [];
    
    if (gstinList.length > 0) {
      // PAN has linked GSTINs - ask user to select one
      const gstinOptions = gstinList.map(item => 
        `<option value="${item.gstin}">${item.gstin} (${item.authStatus || 'N/A'} - State: ${item.stateCd || 'N/A'})</option>`
      ).join('');
      
      Swal.fire({
        icon: 'success',
        title: 'PAN Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-3"><strong>PAN:</strong> ${panNo}</p>
            <p class="mb-2 font-semibold">This PAN has ${gstinList.length} linked GSTIN(s):</p>
            <select id="gstin-select" class="w-full border border-gray-300 rounded-md p-2 mb-3">
              <option value="">-- Select a GSTIN --</option>
              ${gstinOptions}
            </select>
            <p class="text-sm text-blue-600">💡 Select a GSTIN to auto-fill GST field (no re-verification needed)</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Use Selected GSTIN',
        cancelButtonText: 'Skip',
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#6b7280',
        preConfirm: () => {
          const selectedGSTIN = document.getElementById('gstin-select').value;
          if (!selectedGSTIN) {
            Swal.showValidationMessage('Please select a GSTIN');
            return false;
          }
          return selectedGSTIN;
        }
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          // User selected a GSTIN - set it directly without verification
          setForm(prevForm => ({
            ...prevForm,
            gstNo: result.value
          }));
          // Make GST field read-only as well
          setIsGstReadOnly(true);
          
          Swal.fire({
            icon: 'success',
            title: 'GSTIN Set Successfully!',
            html: `
              <div class="text-left">
                <p class="mb-2"><strong>Selected GSTIN:</strong> ${result.value}</p>
                <p class="text-sm text-green-600 mt-3">✓ GSTIN has been set from PAN verification. No separate verification needed.</p>
                <p class="text-sm text-gray-600 mt-2">ℹ️ Both PAN and GST fields are now locked.</p>
              </div>
            `,
            timer: 3000,
            showConfirmButton: false
          });
        }
      });
    } else {
      // PAN verified but no linked GSTINs
      Swal.fire({
        icon: 'success',
        title: 'PAN Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>PAN:</strong> ${panNo}</p>
            <p class="mb-2"><strong>Status:</strong> ${verificationData.authStatus || 'Active'}</p>
            ${verificationData.stateCd ? `<p class="mb-2"><strong>State Code:</strong> ${verificationData.stateCd}</p>` : ''}
            <p class="text-sm text-yellow-600 mt-3">⚠️ No linked GSTINs found for this PAN.</p>
            <p class="text-sm text-gray-600">ℹ️ PAN field is now locked.</p>
          </div>
        `,
        timer: 3000,
        showConfirmButton: false
      });
    }
  };

  // Open GST verification modal
  const openGSTVerification = () => {
    console.log('Opening GST verification modal (Dealer)');
    setShowGSTVerification(true);
  };

  // -----------------------
  // DEALERS + PAGINATION
  // -----------------------
  const [dealers, setDealers] = useState([]);
  const [filter, setFilter] = useState('');
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDealers, setTotalDealers] = useState(0);

  // -----------------------
  // MODAL FOR VIEWING ONE DEALER
  // -----------------------
  const [selectedDealer, setSelectedDealer] = useState(null);

  // -----------------------
  // FETCH STATIONS
  // -----------------------
  useEffect(() => {
    fetchStations();
  }, []);

  const fetchStations = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/stations`
      );
      setStations(response.data);
    } catch (error) {
      console.error('Failed to fetch stations:', error);
    }
  };

  // Station creation functions
  const resetStationForm = () => {
    setStationForm({
      name: '',
      state: '',
      createdBy: user?.user?._id
    });
  };

  const handleStationFormChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Convert station name to uppercase
    if (name === 'name') {
      processedValue = value.toUpperCase();
    }
    
    setStationForm(prev => ({ ...prev, [name]: processedValue }));
  };

  const validateStationForm = () => {
    const errors = [];
    
    if (!stationForm.name?.trim()) {
      errors.push('Station name is required');
    }
    if (!stationForm.state?.trim()) {
      errors.push('State selection is required');
    }
    
    // Check for duplicate station name
    const duplicate = stations.find(
      station => station.name.toLowerCase() === stationForm.name.toLowerCase()
    );
    if (duplicate) {
      errors.push('A station with this name already exists');
    }
    
    return errors;
  };

  const handleCreateStation = async () => {
    const errors = validateStationForm();
    if (errors.length > 0) {
      Swal.fire('Validation Error', errors.join('\n'), 'error');
      return;
    }

    setIsCreatingStation(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/stations`, {
        name: stationForm.name,
        state: stationForm.state,
        createdBy: stationForm.createdBy
      });
      
      const newStation = response.data.station;
      if (newStation) {
        // Refresh stations list from backend to ensure sync
        await fetchStations();
        
        // Select the newly created station
        setForm(prev => ({ ...prev, station: newStation._id }));
        
        // Reset station form and close modal
        resetStationForm();
        setShowCreateStation(false);
        
        // Show success message
        Swal.fire('Success', `Station "${newStation.name}" created successfully!`, 'success');
      }
    } catch (error) {
      console.error('Error creating station:', error);
      
      let errorMessage = 'Failed to create station';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    } finally {
      setIsCreatingStation(false);
    }
  };

  // -----------------------
  // FETCH DEALERS
  // (Whenever currentPage, pageSize, or filter changes)
  // -----------------------
  useEffect(() => {
    fetchDealers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filter]);

  const fetchDealers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/dealers`,
        {
          params: {
            limit: pageSize,
            page: currentPage,
            filter,
            userCompanyId: user.user.company,
          },
        }
      );
      setDealers(response.data.dealers || []);
      setTotalDealers(response.data.totalDealers || 0);
    } catch (error) {
      console.error('Failed to fetch dealers:', error);
    }
  };

  // -----------------------
  // FORM VALIDATION
  // -----------------------
  const validateForm = () => {
    // Required fields validation
    if (!form.businessName || !form.phone || !form.address || !form.station || !form.pinCode) {
      Swal.fire({
        icon: 'error',
        title: 'Required Fields Missing',
        text: 'Business Name, Phone, Address, Station and Pin Code are required fields.'
      });
      return false;
    }

    // Phone number validation
    if (!/^\d{10}$/.test(form.phone)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Phone Number',
        text: 'Phone number must be exactly 10 digits.'
      });
      return false;
    }

    // Secondary phone validation (if provided)
    if (form.secondaryPhone && !/^\d{10}$/.test(form.secondaryPhone)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Secondary Phone',
        text: 'Secondary phone number must be exactly 10 digits.'
      });
      return false;
    }

    // Email validation (if provided)
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Email',
        text: 'Please enter a valid email address.'
      });
      return false;
    }

    // GST validation (if provided)
    if (form.gstNo && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gstNo)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid GSTIN',
        text: 'Please enter a valid GSTIN number.'
      });
      return false;
    }

    // PAN validation (if provided)
    if (form.panNo && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNo)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid PAN',
        text: 'Please enter a valid PAN number.'
      });
      return false;
    }

    return true;
  };

  // -----------------------
  // ADD OR UPDATE DEALER
  // -----------------------
  const handleAddOrUpdateDealer = async () => {
    if (!validateForm()) return;
    
    try {
      Swal.fire({
        title: `${isEditing ? 'Updating' : 'Adding'} Dealer...`,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const cleanedFormData = cleanFormData(form);

      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/dealers/${editingId}`,
          {
            ...cleanedFormData,
            updatedBy: user?.user?._id,
          }
        );
        Swal.fire('Success!', 'Dealer updated successfully!', 'success');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/dealers`,
          cleanedFormData
        );
        Swal.fire('Success!', 'Dealer added successfully!', 'success');
      }

      // Delete the draft after successful dealer creation/update
      if (draftManager.currentDraftId) {
        try {
          await draftManager.deleteDraft(draftManager.currentDraftId);
          console.log('✅ Draft deleted after successful dealer save');
        } catch (draftError) {
          console.error('⚠️ Failed to delete draft:', draftError);
          // Don't show error to user, draft deletion is not critical
        }
      }

      resetForm();
      fetchDealers();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to save dealer.';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg
      });
    }
  };

  // -----------------------
  // EDIT DEALER
  // -----------------------
  const startEditing = async (dealer) => {
    // Delete any existing draft when editing a dealer
    if (draftManager.currentDraftId) {
      try {
        await draftManager.deleteDraft(draftManager.currentDraftId);
        console.log('✅ Draft deleted when editing dealer');
      } catch (draftError) {
        console.error('⚠️ Failed to delete draft on edit:', draftError);
        // Don't block user flow if draft deletion fails
      }
    }
    
    setIsEditing(true);
    setEditingId(dealer._id);
    
    const formData = {
      businessName: dealer.businessName,
      phone: dealer.phone,
      address: dealer.address,
      station: dealer.station?._id,
      pinCode: dealer.pinCode,
      panNo: dealer.panNo,
      createdBy: dealer.createdBy,
    };

    // Only add optional fields if they have values
    if (dealer.businessOwner?.trim()) formData.businessOwner = dealer.businessOwner;
    if (dealer.secondaryPhone?.trim()) formData.secondaryPhone = dealer.secondaryPhone;
    if (dealer.email?.trim()) formData.email = dealer.email;
    if (dealer.managerName?.trim()) formData.managerName = dealer.managerName;
    if (dealer.gstNo?.trim()) formData.gstNo = dealer.gstNo;

    setForm(formData);
    
    // Check if PAN should be read-only (if GST exists and PAN matches extracted PAN)
    if (dealer.gstNo?.trim() && dealer.gstNo.length === 15 && dealer.panNo?.trim()) {
      const extractedPAN = dealer.gstNo.substring(2, 12);
      if (dealer.panNo === extractedPAN) {
        setIsPanReadOnly(true);
        setIsGstReadOnly(true); // Also set GST as read-only for verified data
        setShowGstPanFields(true); // Show fields for verified data
      } else {
        setIsPanReadOnly(false);
        setIsGstReadOnly(false);
        setShowGstPanFields(dealer.gstNo?.trim() ? true : false); // Show if GST exists
      }
    } else {
      setIsPanReadOnly(false);
      setIsGstReadOnly(false);
      setShowGstPanFields(dealer.gstNo?.trim() ? true : false); // Show if GST exists
    }
    
    // Focus on businessName input after state updates
    setTimeout(() => {
      businessNameRef.current?.focus();
    }, 0);
  };

  // -----------------------
  // VIEW DEALER (Modal)
  // -----------------------
  const handleViewDealer = (dealer) => {
    setSelectedDealer(dealer);
  };

  const closeViewDealerModal = () => {
    setSelectedDealer(null);
  };

  // -----------------------
  // UTILS
  // -----------------------
  const resetForm = () => {
    setForm({
      businessName: '',
      phone: '',
      address: '',
      station: '',
      pinCode: '',
      panNo: '',
      createdBy: user?.user?._id,
      // Optional fields set to empty strings
      businessOwner: '',
      secondaryPhone: '',
      email: '',
      managerName: '',
      gstNo: '',
    });
    setIsEditing(false);
    setEditingId(null);
    setIsPanReadOnly(false); // Reset PAN read-only state
    setIsGstReadOnly(false); // Reset GST read-only state
    setShowGstPanFields(false); // Hide GST/PAN fields on form reset
  };

  const totalPages = Math.ceil(totalDealers / pageSize);

  // Handle input changes with uppercase conversion for relevant fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Convert specific fields to uppercase
    const uppercaseFields = ['businessName', 'businessOwner', 'managerName', 'address'];
    if (uppercaseFields.includes(name)) {
      processedValue = value.toUpperCase();
    }
    
    setForm({ ...form, [name]: processedValue });
  };

  return (
    <div className={`p-3 sm:p-4 md:p-6 shadow-xl rounded-xl md:rounded-2xl transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Header */}
      <div className="mb-4 sm:mb-6 md:mb-8">
        <div className={`flex items-center justify-between p-4 sm:p-5 md:p-6 rounded-lg md:rounded-xl transition-colors duration-300 ${
          darkMode 
            ? 'bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900' 
            : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600'
        }`}>
          <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm p-2 sm:p-2.5 md:p-3 rounded-lg">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                Dealer Management
              </h2>
              <p className="text-blue-100 text-xs sm:text-sm mt-0.5 sm:mt-1">Manage your business partners</p>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-2 bg-white bg-opacity-20 backdrop-blur-sm px-4 py-2 rounded-lg">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-white font-medium">{isEditing ? 'Edit Mode' : 'Create Mode'}</span>
          </div>
        </div>
      </div>

      {/* -------------------------
          ADD / EDIT DEALER SECTION
         ------------------------- */}
      <div className={`mb-4 sm:mb-5 md:mb-6 p-4 sm:p-5 md:p-6 rounded-lg md:rounded-xl border transition-colors duration-300 ${
        darkMode 
          ? 'bg-gray-700 border-gray-600' 
          : 'bg-gradient-to-br from-gray-50 to-white border-gray-200 shadow-md'
      }`}>
        <h3 className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 flex items-center transition-colors duration-300 ${
          darkMode ? 'text-gray-100' : 'text-gray-800'
        }`}>
          <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {isEditing ? 'Edit Dealer' : 'Add New Dealer'}
        </h3>        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* businessName */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Business Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="businessName"
              ref={businessNameRef}
              value={form.businessName}
              onChange={handleInputChange}
              placeholder="Enter business name (will be converted to uppercase)"
              className={`border rounded-lg p-2.5 w-full uppercase transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              }`}
              style={{ textTransform: 'uppercase' }}
            />
          </div>
          {/* businessOwner */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Business Owner
            </label>
            <input
              type="text"
              name="businessOwner"
              value={form.businessOwner}
              onChange={handleInputChange}
              placeholder="Enter business owner name (will be converted to uppercase)"
              className={`border rounded-lg p-2.5 w-full uppercase transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              }`}
              style={{ textTransform: 'uppercase' }}
            />
          </div>
          {/* phone */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Phone (Primary) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="phone"
              maxLength={10}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Enter 10-digit mobile number"
              className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
          </div>
          {/* secondaryPhone */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Phone (Secondary)
            </label>
            <input
              type="text"
              name="secondaryPhone"
              maxLength={10}
              value={form.secondaryPhone}
              onChange={(e) =>
                setForm({ ...form, secondaryPhone: e.target.value })
              }
              placeholder="Enter alternate contact number"
              className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
          </div>
          {/* email */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Email
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Enter email address"
              className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
          </div>
          {/* managerName */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Manager Name
            </label>
            <input
              type="text"
              name="managerName"
              value={form.managerName}
              onChange={handleInputChange}
              placeholder="Enter manager name (will be converted to uppercase)"
              className={`border rounded-lg p-2.5 w-full uppercase transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              }`}
              style={{ textTransform: 'uppercase' }}
            />
          </div>
          {/* address */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address"
              value={form.address}
              onChange={handleInputChange}
              placeholder="Enter address (will be converted to uppercase)"
              className={`border rounded-lg p-2.5 w-full uppercase transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              }`}
              style={{ textTransform: 'uppercase' }}
            />
          </div>
          
          {/* station - Using StationSelect component */}
          <StationSelect
            value={form.station}
            onChange={(stationId) => setForm({ ...form, station: stationId })}
            stations={stations}
            onCreateNew={() => {
              resetStationForm();
              setShowCreateStation(true);
            }}
            customSelectStyles={customSelectStyles(darkMode)}
            darkMode={darkMode}
            required={true}
            createNewValue="create-new-station"
          />
          
          {/* pinCode */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Pin Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="pinCode"
              value={form.pinCode}
              onChange={(e) => setForm({ ...form, pinCode: e.target.value })}
              placeholder="Enter pin code"
              className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
          </div>
          {/* GST Verification Button or GST/PAN Fields */}
          {!showGstPanFields ? (
            // Show verify buttons when fields are hidden
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  GST Verification
                </label>
                <button
                  type="button"
                  onClick={openGSTVerification}
                  className={`px-6 py-2.5 rounded-lg transition-all duration-300 text-sm flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-[1.02] ${
                    darkMode 
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white' 
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                  }`}
                  title="Verify GST with Government Portal"
                >
                  <span>🔍</span>
                  <span>Verify GST Number</span>
                </button>
                <p className={`text-xs mt-1 transition-colors duration-300 ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Verify GSTIN and auto-extract PAN
                </p>
              </div>
              
              <div className="flex flex-col">
                <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  PAN Verification
                </label>
                <button
                  type="button"
                  onClick={() => setShowPANVerification(true)}
                  className={`px-6 py-2.5 rounded-lg transition-all duration-300 text-sm flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-[1.02] ${
                    darkMode 
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 text-white' 
                      : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white'
                  }`}
                  title="Verify PAN directly with Government Portal"
                >
                  <span>🔍</span>
                  <span>Verify PAN Number</span>
                </button>
                <p className={`text-xs mt-1 transition-colors duration-300 ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Verify PAN number independently
                </p>
              </div>
            </div>
          ) : (
            // Show GST and PAN fields after verification
            <>
              {/* gstNo */}
              <div className="flex flex-col">
                <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  GST No {isGstReadOnly && <span className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>(Verified from GST Portal) 🔒</span>}
                </label>
                <div className="flex space-x-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      name="gstNo"
                      value={form.gstNo}
                      onChange={handleGSTChange}
                      placeholder="Enter 15-digit GST number"
                      maxLength="15"
                      disabled={isGstReadOnly}
                      className={`border rounded-lg p-2.5 w-full pr-10 transition-colors duration-300 ${
                        isGstReadOnly 
                          ? darkMode 
                            ? 'bg-gray-600 text-gray-300 cursor-not-allowed border-gray-500' 
                            : 'bg-gray-200 text-gray-600 cursor-not-allowed border-gray-300'
                          : darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500'
                      }`}
                    />
                    {form.gstNo && (
                      <button
                        type="button"
                        onClick={handleClearGST}
                        className={`absolute right-2 top-1/2 transform -translate-y-1/2 transition-colors ${
                          darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                        }`}
                        title="Clear GST and PAN"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={openGSTVerification}
                    disabled={isGstReadOnly}
                    className={`px-3 py-2 text-white rounded-lg transition-all duration-300 text-sm whitespace-nowrap shadow-md ${
                      isGstReadOnly 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg'
                    }`}
                    title={isGstReadOnly ? "GST already verified" : "Verify GST with Government Portal"}
                  >
                    {isGstReadOnly ? '✅ Verified' : '🔍 Verify GST'}
                  </button>
                </div>
              </div>
              
              {/* panNo */}
              <div className="flex flex-col">
                <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  PAN No {isPanReadOnly && <span className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>(Auto-extracted from GST) 🔒</span>}
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    name="panNo"
                    value={form.panNo}
                    onChange={handlePANChange}
                    disabled={isPanReadOnly}
                    placeholder="Enter PAN or provide GST to auto-extract"
                    maxLength="10"
                    className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                      isPanReadOnly 
                        ? darkMode 
                          ? 'bg-gray-600 text-gray-300 cursor-not-allowed border-gray-500' 
                          : 'bg-gray-200 text-gray-600 cursor-not-allowed border-gray-300'
                        : darkMode 
                          ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPANVerification(true)}
                    disabled={isPanReadOnly}
                    className={`px-3 py-2 text-white rounded-lg transition-colors text-sm whitespace-nowrap ${
                      isPanReadOnly 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                    title={isPanReadOnly ? "PAN already verified from GST" : "Verify PAN with Government Portal"}
                  >
                    {isPanReadOnly ? '✅ From GST' : '🔍 Verify PAN'}
                  </button>
                </div>
              </div>
            </>
          )}        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-4 sm:mt-6">
          <button
            onClick={handleAddOrUpdateDealer}
            onDoubleClick={(e) => e.preventDefault()}
            className="group relative bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isEditing ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              )}
            </svg>
            <span>{isEditing ? 'Update Dealer' : 'Add Dealer'}</span>
          </button>
          {isEditing && (
            <button
              onClick={resetForm}
              className={`px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg ${
                darkMode 
                  ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                  : 'bg-gray-400 hover:bg-gray-500 text-white'
              }`}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* -------------------------
          SEARCH + PAGE SIZE
         ------------------------- */}
      <div className={`mb-4 sm:mb-5 md:mb-6 p-3 sm:p-4 rounded-lg md:rounded-xl border transition-colors duration-300 ${
        darkMode 
          ? 'bg-gray-700 border-gray-600' 
          : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <input
            type="text"
            placeholder="Search by business name or phone..."
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
            className={`border rounded-lg p-2.5 w-full sm:w-2/3 md:w-1/2 transition-colors duration-300 ${
              darkMode 
                ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            }`}
          />
          <Select
            value={pageSizeOptions.find(option => option.value === pageSize)}
            onChange={(selectedOption) => {
              setPageSize(selectedOption.value);
              setCurrentPage(1);
            }}
            options={pageSizeOptions}
            styles={customSelectStyles(darkMode)}
            className="w-full sm:w-1/3 md:w-1/4"
            isSearchable={false}
          />
        </div>
      </div>      {/* -------------------------
          DEALER LIST (Desktop Table)
         ------------------------- */}
      <div className="overflow-x-auto hidden lg:block">
        <div className={`rounded-xl shadow-lg border overflow-hidden transition-colors duration-300 ${
          darkMode 
            ? 'bg-gray-700 border-gray-600' 
            : 'bg-white border-gray-200'
        }`}>
          <table className="w-full">
            <thead className={`border-b transition-colors duration-300 ${
              darkMode 
                ? 'bg-gradient-to-r from-gray-700 to-gray-800 border-gray-600' 
                : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-200'
            }`}>
              <tr>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Dealer ID</th>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Business Name</th>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Primary Phone Number</th>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Created By</th>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y transition-colors duration-300 ${
              darkMode ? 'divide-gray-600' : 'divide-gray-200'
            }`}>
              {dealers?.map((dealer, index) => (
                <tr key={dealer._id} className={`transition-colors duration-200 ${
                  darkMode 
                    ? `hover:bg-gray-600 ${index % 2 === 0 ? 'bg-gray-700' : 'bg-gray-700/50'}`
                    : `hover:bg-blue-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`
                }`}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-blue-400' : 'text-blue-600'
                  }`}>{dealer.id}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-900'
                  }`}>{dealer.businessName}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{dealer.phone}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <div>
                      <div className="font-medium">{dealer.createdBy?.name || 'N/A'}</div>
                      {dealer.company?.hasSeparateCRM === false && dealer.company?.name && (
                        <div className={`text-xs transition-colors duration-300 ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>{dealer.company.name}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEditing(dealer)}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center gap-1 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                      >
                        <FaEdit className="text-xs" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleViewDealer(dealer)}
                        className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-3 py-2 rounded-lg hover:from-gray-600 hover:to-gray-700 flex items-center gap-1 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                      >
                        <FaEye className="text-xs" />
                        View                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>      {/* -------------------------
          DEALER LIST (Tablet Layout)
         ------------------------- */}
      <div className="hidden md:block lg:hidden">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Business Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Station</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {dealers?.map((dealer, index) => (
                <tr key={dealer._id} className={`hover:bg-blue-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">{dealer.id}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">{dealer.businessName}</div>
                    <div className="text-xs text-gray-500 truncate" title={dealer.address}>
                      {dealer.address}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <div>{dealer.phone}</div>
                    {dealer.secondaryPhone && (
                      <div className="text-xs text-gray-500">{dealer.secondaryPhone}</div>
                    )}
                  </td>                  <td className="px-4 py-3 text-sm text-gray-700">
                    {dealer.station?.name || 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <div className="font-medium">{dealer.createdBy?.name || 'N/A'}</div>
                    {dealer.company?.hasSeparateCRM === false && dealer.company?.name && (
                      <div className="text-xs text-gray-500">{dealer.company.name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => startEditing(dealer)}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 py-1 rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center justify-center gap-1 text-xs transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <FaEdit />
                        Edit
                      </button>
                      <button
                        onClick={() => handleViewDealer(dealer)}
                        className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-2 py-1 rounded-lg hover:from-gray-600 hover:to-gray-700 flex items-center justify-center gap-1 text-xs transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <FaEye />
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>{/* -------------------------
          MOBILE CARDS
         ------------------------- */}      <div className="block md:hidden mt-4 space-y-3">        {dealers?.map((dealer) => (
          <div
            key={dealer._id}
            className={`border rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 ${
              darkMode 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1 min-w-0 pr-2">
                <div className={`font-semibold text-base sm:text-lg truncate ${
                  darkMode ? 'text-gray-100' : 'text-gray-800'
                }`}>
                  {dealer.businessName}
                </div>
                <div className={`text-xs sm:text-sm font-medium ${
                  darkMode ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  ID: {dealer.id}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => startEditing(dealer)}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2.5 sm:px-3 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center gap-1 text-xs sm:text-sm transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <FaEdit className="text-xs sm:text-sm" />
                  <span className="hidden xs:inline">Edit</span>
                </button>
                <button
                  onClick={() => handleViewDealer(dealer)}
                  className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-2.5 sm:px-3 py-2 rounded-lg hover:from-gray-600 hover:to-gray-700 flex items-center gap-1 text-xs sm:text-sm transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <FaEye className="text-xs sm:text-sm" />
                  <span className="hidden xs:inline">View</span>
                </button>              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3 text-xs sm:text-sm">
              <div className={`p-2.5 sm:p-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-blue-900/40 border border-blue-700/50' 
                  : 'bg-blue-50 border border-blue-100'
              }`}>
                <span className={`font-medium block mb-1 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Phone:</span>
                <div className={`font-medium break-all ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{dealer.phone}</div>
              </div>
              <div className={`p-2.5 sm:p-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-purple-900/40 border border-purple-700/50' 
                  : 'bg-purple-50 border border-purple-100'
              }`}>
                <span className={`font-medium block mb-1 ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>Secondary:</span>
                <div className={`break-all ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{dealer.secondaryPhone || '-'}</div>
              </div>
              <div className={`col-span-2 p-2.5 sm:p-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-green-900/40 border border-green-700/50' 
                  : 'bg-green-50 border border-green-100'
              }`}>
                <span className={`font-medium block mb-1 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>Address:</span>
                <div className={`${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{dealer.address}</div>
              </div>
              <div className={`p-2.5 sm:p-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-amber-900/40 border border-amber-700/50' 
                  : 'bg-amber-50 border border-amber-100'
              }`}>
                <span className={`font-medium block mb-1 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>Station:</span>
                <div className={`truncate ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{dealer.station?.name || 'N/A'}</div>
              </div>
              <div className={`p-2.5 sm:p-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-indigo-900/40 border border-indigo-700/50' 
                  : 'bg-indigo-50 border border-indigo-100'
              }`}>
                <span className={`font-medium block mb-1 ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>Created By:</span>
                <div className={darkMode ? 'text-gray-100' : 'text-gray-800'}>
                  <div className="font-medium truncate">{dealer.createdBy?.name || 'N/A'}</div>
                  {dealer.company?.hasSeparateCRM === false && dealer.company?.name && (
                    <div className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{dealer.company.name}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>      {/* -------------------------
          PAGINATION CONTROLS
         ------------------------- */}
      <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0 mt-6 sm:mt-8 p-3 sm:p-4 rounded-lg shadow-sm border transition-colors duration-200 ${
        darkMode 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <div className={`text-xs sm:text-sm text-center sm:text-left ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
          <span className="font-medium">{Math.min(currentPage * pageSize, totalDealers)}</span> of{' '}
          <span className="font-medium">{totalDealers}</span> dealers
        </div>
        
        <div className="flex items-center space-x-1 sm:space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className={`inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
              darkMode
                ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600 disabled:bg-gray-800'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 disabled:bg-gray-100'
            }`}
          >
            <FaChevronLeft className="mr-1 sm:mr-2 h-3 w-3" />
            <span className="hidden xs:inline">Previous</span>
            <span className="xs:hidden">Prev</span>
          </button>
          
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
                    currentPage === pageNum
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md transform scale-105'
                      : darkMode
                      ? 'text-gray-300 hover:bg-gray-700 border border-gray-600'
                      : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => {
              if (currentPage < totalPages) {
                setCurrentPage(currentPage + 1);
              }
            }}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
              darkMode
                ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600 disabled:bg-gray-800'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 disabled:bg-gray-100'
            }`}
          >
            <span className="hidden xs:inline">Next</span>
            <FaChevronRight className="ml-1 sm:ml-2 h-3 w-3" />
          </button>
        </div>
      </div>

      {/* -------------------------
          VIEW DEALER DETAILS MODAL - Enhanced Styling
         ------------------------- */}      {selectedDealer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-3 sm:p-4">
          <div className={`rounded-xl w-full max-w-md sm:max-w-lg lg:max-w-2xl mx-auto relative shadow-2xl transform transition-all duration-300 ease-in-out max-h-[95vh] sm:max-h-[90vh] flex flex-col ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Modal header with gradient */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-4 sm:px-6 py-4 sm:py-5 rounded-t-xl flex justify-between items-center flex-shrink-0 shadow-lg">
              <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2 sm:gap-3">
                <span className="bg-white/20 backdrop-blur-sm rounded-full p-2 shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </span>
                <span>Dealer Details</span>
              </h2>
              <button
                className="text-white hover:bg-white/20 focus:outline-none transition-all rounded-full p-2 hover:rotate-90 duration-300"
                onClick={closeViewDealerModal}
                aria-label="Close"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal content - Scrollable */}
            <div className={`overflow-y-auto flex-1 p-4 sm:p-6 space-y-4 ${
              darkMode ? 'bg-gray-800' : 'bg-gray-50'
            }`}>
              {/* Business Name - Prominent Display */}
              <div className={`rounded-xl p-4 sm:p-5 text-center shadow-md ${
                darkMode 
                  ? 'bg-gradient-to-br from-gray-700 to-gray-800 border border-gray-600' 
                  : 'bg-gradient-to-br from-white to-gray-50 border border-gray-200'
              }`}>
                <p className={`text-xs sm:text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Business Name</p>
                <h3 className={`text-xl sm:text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>{selectedDealer.businessName}</h3>
                <p className={`text-xs sm:text-sm mt-2 font-medium ${
                  darkMode ? 'text-blue-400' : 'text-blue-600'
                }`}>ID: {selectedDealer.id}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Business Information */}
                <div className={`rounded-xl p-4 sm:p-5 border-l-4 shadow-md transition-all hover:shadow-lg ${
                  darkMode 
                    ? 'bg-blue-900/20 border-blue-500' 
                    : 'bg-blue-50 border-blue-500'
                }`}>
                  <h3 className={`font-semibold mb-3 text-sm sm:text-base flex items-center gap-2 ${
                    darkMode ? 'text-blue-300' : 'text-blue-800'
                  }`}>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Business Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Business Owner</p>
                      <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{selectedDealer.businessOwner || '-'}</p>
                    </div>
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Manager Name</p>
                      <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{selectedDealer.managerName || '-'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Contact Information */}
                <div className={`rounded-xl p-4 sm:p-5 border-l-4 shadow-md transition-all hover:shadow-lg ${
                  darkMode 
                    ? 'bg-purple-900/20 border-purple-500' 
                    : 'bg-purple-50 border-purple-500'
                }`}>
                  <h3 className={`font-semibold mb-3 text-sm sm:text-base flex items-center gap-2 ${
                    darkMode ? 'text-purple-300' : 'text-purple-800'
                  }`}>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Primary Phone</p>
                      <p className={`text-sm sm:text-base font-medium flex items-center gap-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        <svg className={`w-4 h-4 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {selectedDealer.phone}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Secondary Phone</p>
                      <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        {selectedDealer.secondaryPhone || '-'}
                      </p>
                    </div>
                    {selectedDealer.email && (
                      <div>
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Email</p>
                        <p className={`text-sm sm:text-base font-medium flex items-center gap-2 break-all ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          <svg className={`w-4 h-4 flex-shrink-0 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {selectedDealer.email}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Location Information */}
                <div className={`rounded-xl p-4 sm:p-5 border-l-4 shadow-md transition-all hover:shadow-lg ${
                  darkMode 
                    ? 'bg-green-900/20 border-green-500' 
                    : 'bg-green-50 border-green-500'
                }`}>
                  <h3 className={`font-semibold mb-3 text-sm sm:text-base flex items-center gap-2 ${
                    darkMode ? 'text-green-300' : 'text-green-800'
                  }`}>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Location Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Address</p>
                      <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        {selectedDealer.address}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Station</p>
                        <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          {selectedDealer.station?.name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pin Code</p>
                        <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{selectedDealer.pinCode}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Legal Information */}
                <div className={`rounded-xl p-4 sm:p-5 border-l-4 shadow-md transition-all hover:shadow-lg ${
                  darkMode 
                    ? 'bg-amber-900/20 border-amber-500' 
                    : 'bg-amber-50 border-amber-500'
                }`}>
                  <h3 className={`font-semibold mb-3 text-sm sm:text-base flex items-center gap-2 ${
                    darkMode ? 'text-amber-300' : 'text-amber-800'
                  }`}>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Legal Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>GST Number</p>
                      <p className={`text-sm sm:text-base font-medium font-mono ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{selectedDealer.gstNo || '-'}</p>
                    </div>
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>PAN Number</p>
                      <p className={`text-sm sm:text-base font-medium font-mono ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{selectedDealer.panNo || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Information */}
              <div className={`rounded-xl p-4 sm:p-5 border-l-4 shadow-md transition-all hover:shadow-lg ${
                darkMode 
                  ? 'bg-indigo-900/20 border-indigo-500' 
                  : 'bg-indigo-50 border-indigo-500'
              }`}>
                <h3 className={`font-semibold mb-3 text-sm sm:text-base flex items-center gap-2 ${
                  darkMode ? 'text-indigo-300' : 'text-indigo-800'
                }`}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  System Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Created By</p>
                    <p className={`text-sm sm:text-base font-medium flex items-center gap-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                      <svg className={`w-4 h-4 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {selectedDealer.createdBy?.name || 'N/A'}
                    </p>
                  </div>
                  {selectedDealer.company?.hasSeparateCRM === false && selectedDealer.company?.name && (
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Company</p>
                      <p className={`text-sm sm:text-base font-medium flex items-center gap-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        <svg className={`w-4 h-4 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {selectedDealer.company.name}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Modal footer */}
            <div className={`px-4 sm:px-6 py-4 rounded-b-xl flex justify-center gap-3 flex-shrink-0 border-t ${
              darkMode 
                ? 'bg-gray-700 border-gray-600' 
                : 'bg-gray-100 border-gray-200'
            }`}>
              <button
                onClick={closeViewDealerModal}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:scale-105 ${
                  darkMode
                    ? 'bg-gray-600 text-white hover:bg-gray-500 focus:ring-gray-500'
                    : 'bg-white text-gray-700 hover:bg-gray-50 focus:ring-gray-400 border border-gray-300'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Station Creation Modal */}
      {showCreateStation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Create New Station</h2>
                    <p className="text-blue-100 text-sm">Add a new station to the system</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowCreateStation(false);
                    resetStationForm();
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Station Information Section */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                    <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    </svg>
                  </div>
                  Station Details
                </h3>
                
                <div className="space-y-4">
                  {/* Station Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Station Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={stationForm.name}
                      onChange={handleStationFormChange}
                      placeholder="Enter station name (will be converted to uppercase)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>

                  {/* State */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="state"
                      value={stationForm.state}
                      onChange={handleStationFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select State</option>
                      {INDIAN_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowCreateStation(false);
                    resetStationForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  disabled={isCreatingStation}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateStation}
                  disabled={isCreatingStation || !stationForm.name?.trim() || !stationForm.state?.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
                >
                  {isCreatingStation ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                      </svg>
                      Create Station
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GST Verification Modal */}
      <GSTVerificationModal
        isOpen={showGSTVerification}
        onClose={() => setShowGSTVerification(false)}
        onDataReceived={handleGSTVerificationData}
        initialGSTIN={form.gstNo}
      />

      {/* PAN Verification Modal */}
      <PANVerificationModal
        isOpen={showPANVerification}
        onClose={() => setShowPANVerification(false)}
        onDataReceived={handlePANVerificationData}
        initialPAN={form.panNo}
      />

      {/* Auto-save Notification */}
      {(draftManager.autoSaving || showNotification) && (
        <div className="fixed top-20 right-4 z-50 animate-fade-in">
          <div 
            className={`rounded-lg shadow-2xl px-4 py-3 min-w-[250px] border-l-4 cursor-pointer transition-transform hover:scale-105 ${
              draftManager.autoSaving 
                ? 'bg-blue-50 border-blue-500' 
                : 'bg-green-50 border-green-500'
            }`}
            onClick={() => !draftManager.autoSaving && setShowNotification(false)}
            title={!draftManager.autoSaving ? 'Click to dismiss' : ''}
          >
            <div className="flex items-center gap-3">
              <div className={`text-2xl ${draftManager.autoSaving ? 'animate-spin' : ''}`}>
                {draftManager.autoSaving ? '🔄' : '✅'}
              </div>
              <div className="flex-1">
                <p className={`font-semibold text-sm ${
                  draftManager.autoSaving 
                    ? 'text-blue-800' 
                    : 'text-green-800'
                }`}>
                  {draftManager.autoSaving ? 'Auto-saving...' : notificationMessage}
                </p>
                {draftManager.lastSaved && !draftManager.autoSaving && (
                  <p className="text-xs mt-0.5 text-gray-600">
                    {draftManager.lastSaved.toLocaleTimeString('en-IN')}
                  </p>
                )}
              </div>
              {!draftManager.autoSaving && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNotification(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealerManagement;
